<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import NotificationItem from '@components/NotificationItem';

export default {
  name: 'NotificationCenter',
  components: {
    FontAwesomeIcon,
    NotificationItem,
  },
  data() {
    return {
      showDropdown: false,
    };
  },
  computed: {
    notificationsLoading() {
      return this.$store.state.notifications.loading;
    },
    notifications() {
      return this.$store.state.notifications.notifications || [];
    },
    unreadCount() {
      return this.unreadNotifications.length;
      // return this.$store.state.notifications.unreadCount || 0;
    },
    hasUnread() {
      return this.unreadCount > 0;
    },
    unreadNotifications() {
      return this.notifications.filter(n => !n.read_at);
    },
    recentNotifications() {
      // Show last 10 notifications
      return this.notifications.slice(0, 10);
    },
  },
  async mounted() {
    // Notifications are already initialized after login, no need to fetch again
    // Just ensure we have the latest data
    if (!this.$store.state.notifications.initialized) {
      await this.$store.dispatch('notifications/fetchNotifications');
    }
  },
  methods: {
    async markAllAsRead() {
      if (!this.hasUnread) return;

      try {
        await this.$store.dispatch('notifications/markAllAsRead');
        this.$toasted.info('All notifications marked as read', {
          title: 'Notifications',
          variant: 'success',
          autoHideDelay: 3000,
        });
      } catch (error) {
        this.$toasted.error('Failed to mark notifications as read', {
          title: 'Error',
          variant: 'danger',
          autoHideDelay: 5000,
        });
      }
    },
    onNotificationAction() {
      // Close dropdown when user interacts with notification
      this.showDropdown = false;
    },
    async onDropdownShow() {
      this.showDropdown = true;
    },
    onDropdownHide() {
      this.showDropdown = false;
    },
    goToNotificationsPage() {
      // @TODO Create page for notifications
      // this.$router.push({ name: 'notifications.index' });
      this.showDropdown = false;
    },
  },
};
</script>

<template>
  <b-dropdown
    no-caret
    variant="bg-dark"
    :class="{ 'notification-dropdown': true }"
    @show="onDropdownShow"
    @hide="onDropdownHide"
  >
    <template #button-content>
      <div class="position-relative">
        <FontAwesomeIcon
          :icon="['fa', 'bell']"
          :class="[
            'text-white d-sm-block mr-2',
            { 'text-warning': hasUnread }
          ]"
        />

        <!-- Unread Badge -->
        <b-badge
          v-if="notifications.length > 0"
          variant="danger"
          pill
          class="notification-badge position-absolute"
        >
          {{ notifications.length > 99 ? '99+' : notifications.length }}
        </b-badge>

        <!-- Pulse animation for new notifications -->
        <span
          v-if="hasUnread"
          class="notification-pulse position-absolute"
        ></span>
      </div>
    </template>

    <!-- Loading State -->
    <div v-if="notificationsLoading && !notifications.length" class="text-center p-3">
      <b-spinner small></b-spinner>
      <div class="mt-2 text-muted"> {{ $t('infos.loading_notifications') }} </div>
    </div>

    <!-- No Notifications -->
    <div v-else-if="!notifications.length" class="text-center p-4 text-muted">
      <FontAwesomeIcon :icon="['fa', 'bell-slash']" size="2x" class="mb-2" />
      <div>{{ $t('infos.empty_notifications') }}</div>
    </div>

    <!-- Notifications List -->
    <div v-else class="notifications-list">
      <div
        v-for="notification in recentNotifications"
        :key="notification.id"
        class="notification-dropdown-item"
      >
        <NotificationItem
          :notification="notification"
          :compact="true"
          @mark-read="onNotificationAction"
          @dismiss="onNotificationAction"
          @navigate="onNotificationAction"
          @snooze="onNotificationAction"
        />
      </div>

      <!-- Show More Link -->
      <b-dropdown-divider v-if="notifications.length > 10"></b-dropdown-divider>
      <b-dropdown-item
        v-if="notifications.length > 10"
        class="text-center"
        @click="goToNotificationsPage"
      >
        <FontAwesomeIcon :icon="['fa', 'ellipsis-h']" class="mr-1" />
        View all notifications ({{ notifications.length }})
      </b-dropdown-item>
    </div>
  </b-dropdown>
</template>

<style lang="scss" scoped>

.notification-badge {
  top: -10px;
  right: -5px;
  font-size: 0.7rem;
  min-width: 18px;
  height: 18px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99;
}

.notification-pulse {
  top: -10px;
  right: -4px;
  width: 18px;
  height: 18px;
  background-color: #dc3545;
  border-radius: 50%;
  animation: pulse 2s infinite;
  z-index: 90;
}

@keyframes pulse {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7);
  }

  70% {
    transform: scale(1);
    box-shadow: 0 0 0 10px rgba(220, 53, 69, 0);
  }

  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(220, 53, 69, 0);
  }
}

.notifications-list {
  .notification-dropdown-item {
    &:last-child {
      border-bottom: none;
    }
  }
}
</style>
<style lang="scss">
.notification-dropdown .dropdown-menu {
  width: 400px;
  max-height: 600px;
  overflow-y: auto;
  padding: 0;
  left: -150px;

  @media (max-width: 576px) {
    width: 320px;
  }
}
</style>
